import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiClient } from "../../../zodios/client";
import { toast, type Id } from "react-toastify";
import type { AdPropertyUpdate } from "../../../zodios/apiDefinitions/api/ads";
import { roasToAcos } from "../../../components/core/blocks/AdPlan/utils";
import { invalidateAdQueries } from "../../perf_reports/api/ads/invalidateAdQueries";

export function useBulkUpdateAdProperties() {
  const queryClient = useQueryClient();
  const { t } = useTranslation(["advertising"]);
  return useMutation({
    mutationFn: async (
      vars: (Omit<AdPropertyUpdate, "custom_api_request"> & {
        target_roas?: number;
        min_roas?: number;
      })[],
    ) => {
      let toastId: Id | null = null;
      for (let i = 0; i < vars.length; i++) {
        const change = vars[i]!;
        if (change.target_roas !== undefined) {
          change.target_acos = roasToAcos(change.target_roas);
        }
        if (change.min_roas !== undefined) {
          change.max_acos = roasToAcos(change.min_roas);
        }
        const progress = (i + 1) / vars.length;
        try {
          await apiClient.updateAdProperties({
            custom_api_request: "update_advertisement",
            ...change,
          });
        } catch {
          toast(
            t(($) => $.advertising["Failed to update Media Plan {id}"], { id: change.id }),
            { type: "error" },
          );
        } finally {
          if (!toastId) {
            toastId = toast(
              t(($) => $.advertising["Updating Media Plans"], { count: vars.length }),
              {
                progress,
                type: "info",
              },
            );
          } else {
            toast.update(toastId, { progress });
          }
        }
      }
      if (toastId) {
        toast.update(toastId, {
          render: t(($) => $.advertising["Media Plans Updated"], { count: vars.length }),
          type: "success",
          progress: undefined,
        });
      }
    },
    onSuccess: () => {
      invalidateAdQueries(queryClient);
    },
  });
}
